/**
 * Optional analytics loaders, gated by Consent Mode V2.
 *
 * Reads two Vite env vars (both optional):
 *   VITE_GA_MEASUREMENT_ID   e.g. "G-XXXXXXXXXX"
 *   VITE_GTM_ID              e.g. "GTM-XXXXXXX"
 *
 * Each loader is idempotent: calling it more than once is a no-op. They
 * MUST be called only after the user has granted analytics consent — the
 * cookie consent provider does this for us. If the env var is missing the
 * loader is a no-op too, so the site ships fine without an ID configured.
 *
 * GA4 itself respects the gtag('consent', ...) signals set in
 * `client/index.html` (default denied) and `lib/consent.ts` (update on
 * user choice), so even if the script is loaded with consent denied it
 * will run in "consent mode" without storing identifiers.
 */

import { readStoredConsent } from "./consent";

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;

let gaLoaded = false;
let gtmLoaded = false;

// Config amministrabile: gli ID GA4/GTM possono essere gestiti dal pannello
// admin (Integrazioni → Google Analytics) invece che da variabili d'ambiente.
// Il fetch è lazy + memoizzato; l'env Vite resta come fallback.
let remoteConfigPromise: Promise<{ gaMeasurementId: string | null; gtmId: string | null }> | null =
  null;

function fetchRemoteConfig() {
  if (!remoteConfigPromise) {
    remoteConfigPromise = fetch("/api/public-config")
      .then((r) => (r.ok ? r.json() : { gaMeasurementId: null, gtmId: null }))
      .catch(() => ({ gaMeasurementId: null, gtmId: null }));
  }
  return remoteConfigPromise;
}

function ensureDataLayer(): void {
  if (typeof window === "undefined") return;
  if (!window.dataLayer) window.dataLayer = [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtagShim(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
}

export function loadGoogleAnalytics(): void {
  if (gaLoaded) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (GA_ID) {
    loadGaWithId(GA_ID);
    return;
  }
  // Nessun env: prova la config amministrabile dal server.
  void fetchRemoteConfig().then((cfg) => {
    if (cfg.gaMeasurementId) loadGaWithId(cfg.gaMeasurementId);
  });
}

function loadGaWithId(id: string): void {
  if (gaLoaded || readStoredConsent()?.prefs.analytics !== true) return;

  ensureDataLayer();

  const tag = document.createElement("script");
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    id,
  )}`;
  document.head.appendChild(tag);

  window.gtag?.("js", new Date());
  // anonymize_ip is on by default in GA4; we still set it explicitly so the
  // intent is auditable from the page source. URL passthrough lets GA4
  // measure conversions when ad_storage is denied (Consent Mode V2).
  window.gtag?.("config", id, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    // Don't expose URL query parameters (which may contain contact details)
    // in the automatic GA4 page_view payload.
    page_location: `${window.location.origin}${window.location.pathname}`,
  });

  gaLoaded = true;
}

export function loadGoogleTagManager(): void {
  if (gtmLoaded) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (GTM_ID) {
    loadGtmWithId(GTM_ID);
    return;
  }
  void fetchRemoteConfig().then((cfg) => {
    if (cfg.gtmId) loadGtmWithId(cfg.gtmId);
  });
}

function loadGtmWithId(id: string): void {
  if (gtmLoaded || readStoredConsent()?.prefs.analytics !== true) return;

  ensureDataLayer();
  window.dataLayer?.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const tag = document.createElement("script");
  tag.async = true;
  tag.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    id,
  )}`;
  document.head.appendChild(tag);

  gtmLoaded = true;
}

export const analyticsConfig = {
  hasGa: Boolean(GA_ID),
  hasGtm: Boolean(GTM_ID),
};
